import { IAppState } from '../IAppState';
import { RSAA } from 'redux-api-middleware';
import { Action } from 'redux';
import { ACTION_TYPE } from '../actionType';
import * as R from 'ramda';

export const HTTP_STATUS = {
  OK: 200,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  NOT_FOUND: 404,
  PAYLOAD_TOO_LARGE: 413,
  UNPROCESSABLE_ENTITY: 422,
  CLIENT_CLOSED_REQUEST: 499, // Non-standard
}

export interface FluxStandardAction extends Action {
  /**
   * Default: the response data deserialized as JSON
   */
  payload?: any,
  meta?: any,
  /**
   * Only a value of `true` is seen to be error-y
   */
  error?: any,
}

export type TypeDescriptor = {
  type: string | symbol,
  payload?: any,
  meta?: any,
}

export type RsaaTypes = [
  string | symbol | TypeDescriptor,
  string | symbol | TypeDescriptor,
  string | symbol | TypeDescriptor
  ];


export type ReduxStandardApiAction<T> = {
  /**
   * Endpoint to hit, or a function to call that results in an endpoint to hit.
   */
  endpoint: string | ((state: T) => string),
  method: 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS',
  headers?: object | ((state: T) => object),
  body?: object | string | ((state: T) => object | string),
  credentials?: 'omit' | 'same-origin' | 'include',
  /**
   * object or function producing an object of options to pass to the Fetch API.
   * See: https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch
   * for the list of available options.
   */
  options?: object | ((state: T) => object),
  /**
   * Function/bool for whether or not to bail on a REST request.
   * True = do not perform network request.
   */
  bailout?: boolean | ((state: T) => boolean),
  /**
   * Length 3 array of types to assign to the API request's resulting Flux Standard Action's type property for various
   * circumstances:
   * index 0: REQUEST - Request has been made (yes, two FSAs result from your request action)
   * index 1: RECEIVE - Response has been received
   * index 2: FAILURE - Response was a failure
   * TypeDescriptor objects will have their meta/payload properties merged into the resulting Flux Standard Actions
   */
  types: RsaaTypes,
  fetch?: (input: Request | string, init?: object) => Promise<Response>,
}

/**
 * Constructs an API action with the details of the given Redux Standard API Action object. Only needs to be an object
 * convertible to a RSAA. When this action is dispatched, the redux-api-middleware will grab it up and do the following:
 * 1. Run the bailout function and possibly bail
 * 2. Make the request if we didn't bail
 * 3. Dispatch a Flux Standard Action with the loading state
 * 4. Get a response back
 * 5. Dispatch a Response or Error FSA to indicate the end of the request cycle.
 * @param action - object with the details of the API request to make
 */
export const makeApiAction = (action: ReduxStandardApiAction<IAppState>): Action => ({
  [ RSAA ]: action,
} as any); // Cast to any because Redux wants a `type` prop; RSAA middleware doesn't want a `type` prop.
// TL;DR: redux-api-middleware does not match the strict Redux type spec, so we any to get around that :(


/**
 * A function of the kind accepted by `makeApiReducer` that takes information about a reduced API action such as whether
 * or not there was an HTTP error, whether we're waiting on a response, what the current redux store looks like...
 */
export type ApiReducer<TPayload> = (
  state: IAppState,
  action: FluxStandardAction,
  error: string | undefined,
  isLoading: boolean,
  payload: TPayload | undefined,
) => IAppState;


/**
 * Constructs a reducer for handling actions from ReduxApiMiddleware.
 * @param actions - The list of actions you care about
 * @param loadingAction - The API Middleware's loading action
 * @param withResults - Your reducer logic
 */
export const makeApiReducer = <TPayload>(
  actions: ACTION_TYPE[],
  loadingAction: ACTION_TYPE,
  withResults: ApiReducer<TPayload>,
): (state: IAppState, action: FluxStandardAction) => IAppState => (
  state: IAppState,
  action: FluxStandardAction,
): IAppState => {
  if (!R.includes(action.type, actions)) {
    return state;
  }

  const payload   = R.path<TPayload>([ 'payload' ], action);
  // TODO: Improve error detection... Check status code before using message.
  const error     = action.error || R.path<string>([ 'message' ], payload);
  const isLoading = action.type === loadingAction && !error;

  return withResults(state, action, error, isLoading, payload);
};
